import { rgba } from 'polished';
import React, { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import useOrderContext from '../../../../../contexts/order/useOrderContext';
import useOperatorContext from "../../../../../contexts/operator/useOperatorContext";
import { Item, TSite } from '../../../../../contexts/site/site-context';
import useSiteContext from '../../../../../contexts/site/useSiteContext';
import { getDisplayName } from '../../../../../services/channel';
import { money } from '../../../../../services/format';
import { basketHint, discountLabel, offerHeadline, offerNames, siteNow } from '../../../../../services/offers';
import useOfferEstimate from '../../../../../hooks/useOfferEstimate';
import BasketItem from './BasketItem';
import TipAddBasket from './TipAddBasket';
import PromoForm from '../../../Checkout/components/PromoForm';
import {iLog} from "../../../../../index";
import {ReadyTime, TBasket} from "../../../../../contexts/basket/basket-context";
import mastercard from '../../../../../assets/images/mastercard.svg';
import visa from '../../../../../assets/images/visa.svg';
import amex from '../../../../../assets/images/amex.svg';
import card_icon from '../../../../../assets/images/credit_card_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.svg';
import ServiceCheckbox from "./ServiceCheckbox";
import config from '../../../../../config/channels';


type BasketProps = {
    showTimeslotModal?: (show: boolean) => void;
    checkout?: boolean;
    confirmation?: boolean;
    reset: boolean;
    site: TSite | null | undefined;
    // are the items we receive back the same
};

const Basket = ({ checkout = false, reset = false, site, showTimeslotModal, confirmation = false }: BasketProps) => {
    const basketContext = useBasketContext();
    const siteContext = useSiteContext();
    const operatorContext = useOperatorContext();
    const orderContext = useOrderContext();

    // promo code
    const [promoCode, setpromoCode] = useState('');
    const [promoLoading, setPromoLoading] = useState(false);

    const [bItems, setBItems] = useState<Item[]>([]);
    const [bItemsCount, setBItemsCount] = useState<string>('');
    const [bSite, setBSite] = useState<TSite | null | undefined>(site);

    const [validatedTimeslot, setValidatedTimeslot] = useState<ReadyTime|undefined>(undefined);
    const [showBItemModal, setShowBItemModal] = useState(false);
    const [modalBItem, setModalBItem] = useState<null | Item>(null);
    const [modalBItemIndex, setModalBItemIndex] = useState(-1);

    const [showService, setShowService] = useState(true);
    const [showTipping, setShowTipping] = useState(false);

    const isDineIn = siteContext.channel === config.dineIn.name;

    const format = (pence: number) => money(pence, operatorContext.operator?.ccy, operatorContext.operator?.locale);

    // What the basket gets off from an offer, as far as can be told before it is validated (at checkout it is the API's word that is shown).
    const estimated = useOfferEstimate(basketContext.validatedBasket?.delivery_fee ?? 0);
    const offerEstimate = !checkout && !confirmation ? estimated : null;

    // What the basket is a step away from getting (an offer that works by itself), said while there is still time to get it.
    const offerHint = !checkout && !confirmation && !(basketContext.validatedBasket?.promo_discount ?? 0)
        ? basketHint(siteContext.offers, siteContext.menu, basketContext.basketItems, siteNow(siteContext.site?.timezone), format)
        : null;

    const address = JSON.parse(localStorage.getItem('address') ?? '{}');

    const showItemModal = (item: Item, index: number) => {
        setModalBItem(item);
        setShowBItemModal(true);
        setModalBItemIndex(index);
    };

    const selectServiceByDefault = (validatedBasket: TBasket|null|undefined) => {
        if ( !validatedBasket ) { return false; }
        if ( validatedBasket.service_fee === 0 ) { return false; }
        if ( !validatedBasket.service_optional ) { return true; }
        return validatedBasket.service_default ?? false;
    }

    useEffect(() => {
        orderContext.setServiceSelected( selectServiceByDefault(basketContext.validatedBasket) );
    }, [basketContext.validatedBasket]);


    useEffect(() => {
        switch (basketContext.validatedBasket?.channel ?? 'collection') {
            case 'collection':
                setShowTipping(operatorContext.operator?.theme.v4.tipping_collection ?? false);
                break;
            case 'delivery':
                setShowTipping(operatorContext.operator?.theme.v4.tipping_delivery ?? false);
                break;
            case 'catering':
                setShowTipping(operatorContext.operator?.theme.v4.tipping_catering ?? false);
                break;
            case 'venues':
                setShowTipping(operatorContext.operator?.theme.v4.tipping_venues ?? false);
                break;
            case 'dine_in':
                setShowTipping(operatorContext.operator?.theme.v4.tipping_dine_in ?? false);
                break;
            default:
                setShowTipping(false);
        }

        setShowService(isDineIn && (basketContext.validatedBasket?.service_fee ?? 0) > 0);

        if ( confirmation ) {
            setBItems(orderContext.storedOrder?.basket ?? []);
        } else if (checkout) {
            setBItems(basketContext.validatedBasket?.basket ?? []);
            setValidatedTimeslot(basketContext.validatedBasket?.ready_time);
        } else {
            setBItems(basketContext.basketItems);
        }

        let count = 0;
        bItems.forEach((item: Item) => {
            count += item.quantity ?? 0;
        });

        setBItemsCount('('+ count + ' item' + (Number(count) > 1 ? 's)' : ')'));

    }, [basketContext.basketItems, basketContext.validatedBasket, orderContext.storedOrder, orderContext.serviceSelected]);

    useEffect(() => {
        if (checkout) {
            setBSite(basketContext.validatedSite ?? null);
        } else {
            setBSite(siteContext.site);
        }
    }, [checkout, siteContext.site, basketContext.validatedSite]);

    useEffect(() => {
        if (reset) {
            basketContext.clearBasket();
        }
    }, []);

    const subtotal = () => {
        if ( confirmation ) { return orderContext.storedOrder?.subtotal ?? 0; }
        if ( checkout ) { return basketContext.validatedBasket?.subtotal ?? 0; }
        return basketContext.price.subtotal;
    }

    const service = () => {
        if ( confirmation ) { return orderContext.storedOrder?.service ?? 0; }
        if ( checkout ) { return basketContext.validatedBasket?.service_fee ?? 0; }
        return 0;
    }

    const total = () => {
        if ( confirmation ) { return orderContext.storedOrder?.total ?? 0; }
        if ( checkout ) {

            let base = basketContext.validatedBasket?.total ?? 0;

            if ( isDineIn ) {
                base = orderContext.serviceSelected
                    ? basketContext.validatedBasket?.total_service_selected ?? 0
                    : basketContext.validatedBasket?.total_service_unselected ?? 0
            }

            return base + orderContext.gratuityVal;
        }
        return basketContext.price.total - (offerEstimate?.discount ?? 0);
    }

    const siteName = () => {
        if ( confirmation ) { return orderContext.storedOrder?.site?.name; }
        return basketContext.validatedSite?.name;
    }

    const siteAddress = () => {
        if ( confirmation ) { return `${orderContext.storedOrder?.site?.address_1}, ${orderContext.storedOrder?.site?.address_city}, ${orderContext.storedOrder?.site?.address_postcode}`; }
        return `${basketContext.validatedSite?.address_1}, ${basketContext.validatedSite?.address_city}, ${basketContext.validatedSite?.address_postcode}`;
    }

    const readyTime = () => {
        if ( confirmation ) { return orderContext.order?.ready_time; }
        return basketContext.validatedBasket?.ready_time;
    }

    const tableNo = () => {
        if ( confirmation ) { return orderContext.storedOrder?.table ?? ''; }
        return basketContext.tableNumber;
    }

    const deliveryFee = () => {
        if ( confirmation ) { return orderContext.storedOrder?.delivery.fee ?? 0; }
        return basketContext.validatedBasket?.delivery_fee ?? 0;
    }

    const renderBasket = () => {
        return (
            <BasketItems>
                {bItems.map((item, index) => (
                    <BasketItem
                        key={`basket-${index}-${item.uid}`}
                        handleClick={checkout ? () => {} : () => showItemModal(item, index)}
                        item={item}
                        checkout={checkout || confirmation}
                        last={index === bItems.length - 1}
                    />
                ))}
            </BasketItems>
        );
    };

    const renderDeliveryFee = () => {
        if ( checkout && !confirmation && deliveryFee() === 0 ) {
            return null;
        }

        if ( confirmation && deliveryFee() === 0 ) {
            return null;
        }

        return (
            <div className="basket__subtotal">
                <span className="d-block w-100">{t`Delivery fee`}</span>
                <span>{money(deliveryFee(), operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
            </div>
        );
    };

    const renderGratuity = () => {

        if ( confirmation && (orderContext.storedOrder?.service ?? 0) === 0 ) {
            return null;
        }
        // when not enable via the theme..
        if ( !showTipping ) {
            return null;
        }
        return (
            <TipAddBasket
                id={'tipBasketID'}
                tipValue={orderContext.gratuityVal}
                disabled={false}
                defaultChecked={false}
                selected={orderContext.gratuitySelected}
                isConfirmationPage={confirmation}
            />
        );
    }

    const renderSubtotal = () => {
        return (
            <Subtotal confirmation={confirmation}>
                <div className="basket__subtotal">
                    <span className="d-block w-100">{t`Subtotal`}</span>
                    <span>
                        {money(subtotal(), operatorContext.operator?.ccy, operatorContext.operator?.locale)}
                    </span>
                </div>

                {renderDeliveryFee()}
                {renderOfferEstimate()}
                {renderGiftCardLine()}

            </Subtotal>
        );
    };

    const renderOfferEstimate = () => {
        if ( !offerEstimate ) { return null; }

        return (
            <DiscountLine>
                <div className="basket__subtotal">
                    <span className="d-block w-100">{offerHeadline(offerEstimate.offer, offerNames(offerEstimate.offer, siteContext.menu), format)}</span>
                    <span className="non_wrapped">{money(-offerEstimate.discount, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
                </div>
            </DiscountLine>
        );
    };

    const renderPromoDiscountLine = () => {

        if ( confirmation ) {
            return orderContext.storedOrder?.codes
                .filter((code) => code.amount > 0 )
                .map((code) => {
                    return (
                        <DiscountLine key={code.text}>
                            <div className="basket__subtotal">
                                <span className="d-block w-100">{discountLabel(code, siteContext.menu, format)}</span>
                                <span className="non_wrapped">{money(-code.amount, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
                            </div>
                        </DiscountLine>
                    )
                });
        }

        if ( checkout ) {
            return basketContext.validatedBasket?.codes
                .filter((code) => code.amount > 0 )
                .map((code) => {
                    return (
                        <DiscountLine key={code.text}>
                            <div className="basket__subtotal">
                                <span className="d-block w-100">{discountLabel(code, siteContext.menu, format)}</span>
                                <span className="non_wrapped">{money(-code.amount, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
                            </div>
                        </DiscountLine>
                    )
                });
        }
    };

    const renderGiftCardLine = () => {
        if ( !confirmation ) { return null; }

        if ( !orderContext.storedOrder?.payments?.filter((pa) => pa.type === 'giftcard')[0] ) {
            return null;
        }

        let text = `Giftcard`;
        let discount = orderContext.storedOrder?.payments?.filter((pa) => pa.type === 'giftcard')[0].amount

        return (
            <DiscountLine>
                <div className="basket__subtotal">
                    <span className="d-block w-100">{text}</span>
                    <span className="non_wrapped">{money(-discount, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
                </div>
            </DiscountLine>
        );
    };

    const renderTotal = () => {
        return (
            <Total confirmation={confirmation}>
                <span>{t`Total`}</span>
                <span>{money(total(), operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
            </Total>
        );
    };

    const renderChannelTitle = () => {
        let fromLocation = 'from ' + orderContext.storedOrder?.site?.name;

        if (
            siteContext.site?.channels &&
            Object.keys(siteContext.site?.channels).length === 1 &&
            siteContext.site?.sites_count === 1
        ) {
            fromLocation = '';
        }

        if ( ['collection', 'delivery', 'catering', 'venues'].includes(siteContext.channel) ) {
            let timeslot = readyTime()?.name;
            let timeslot_str = '';

            if (timeslot) {
                const parts = timeslot.split(" ");
                const time = parts.pop(); // Extract the time (last part of the string)
                const rest = parts.join(" "); // Join the remaining parts back into a string

                timeslot_str = `${rest} at ${time}`;

                if ( readyTime()?.is_asap ) {
                    timeslot_str = timeslot;
                }
            }

            iLog('Your timeslot is', [ timeslot, basketContext.validatedBasket?.ready_time ]);

            let displayName = getDisplayName(siteContext.channel);

            if ( basketContext.validatedBasket?.channel === 'catering' && operatorContext.operator?.theme.v4.catering_label ) {
                displayName = operatorContext.operator?.theme.v4.catering_label;
            }

            return (
                <OrderInfo checkout={checkout} className={'basket__order-info basket__order-title'}>
                    <h2 className={"basket__order-info__title"} >{t`Order`}</h2>
                    <div className={'info-card block'}>
                        <span className="info-card__channel">{displayName} &middot; { siteContext.channel === 'collection' ? siteName() : (siteContext.channel === 'venues' ? (address.deliveryVenueName ?? localStorage.getItem('postcode') ?? '/') : (localStorage.getItem('postcode') ?? '/')) }</span>
                        { siteContext.channel === 'collection' && (
                            <span className="info-card__site_address">
                                <a href={`https://www.google.com/maps/search/${siteAddress()}/`} target="_blank">{siteAddress()}</a>
                                <span className="material-symbols-outlined">open_in_new</span>
                            </span>
                        )}
                        <span className="info-card__timeslot" dangerouslySetInnerHTML={{ __html: timeslot_str }}></span>
                    </div>
                </OrderInfo>
            );
        }

        if (siteContext.channel === 'dine_in') {
            return (
                <OrderInfo checkout={checkout} className={'basket__order-info basket__order-title'}>
                    <h2 className={"basket__order-info__title"} >{t`Order`}</h2>
                    <div className={'info-card block'}>
                        <span className="info-card__channel">Table {tableNo()} - {siteName()}</span>
                    </div>
                </OrderInfo>
            );
        }
    };

    const getCardImage = (card:String) => {
        switch (card) {
            case 'visa':
                return visa;
            case 'mastercard':
                return mastercard;
            case 'amex':
                return amex;
            default:
                return card_icon;
        }
    }

    const paymentDetails = () => {
        return {
            last4: orderContext.storedOrder?.payments.filter((pa) => pa.type !== 'giftcard')[0].last4 ?? '',
            brand: orderContext.storedOrder?.payments.filter((pa) => pa.type !== 'giftcard')[0].type ?? ''
        }
    }

    return (
        <BasketContainer checkout={checkout} confirmation={confirmation}>
            <BasketCard className="basket">
                <div>
                    {renderChannelTitle()}
                    <OrderSummary>
                        {bItems.length ? (
                            <>
                                <div className="basketContainer block">
                                    {renderBasket()}
                                    { basketContext.specialNotes && (
                                        <SpecialInstructions>
                                            <span className="material-symbols-outlined">info</span>
                                            <span className="instructions">{ basketContext.specialNotes }</span>
                                        </SpecialInstructions>
                                    )}
                                </div>
                                <div className="subtotalContainer block">
                                    {renderSubtotal()}
                                    <div className="extrasContainer">
                                        { offerHint && (
                                            <OfferHint className="offer-hint">
                                                <span className="material-symbols-outlined material-symbols-outlined--filled">redeem</span>
                                                <span>{offerHint}</span>
                                            </OfferHint>
                                        )}
                                        { !confirmation && (
                                            <div>
                                                <div className="promoContainer">
                                                    <PromoForm />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    { showService && (
                                        <ServiceCheckbox
                                            id={'service'}
                                            tipValue={service()}
                                            disabled={(!basketContext.validatedBasket?.service_optional) ?? false}
                                            defaultChecked={selectServiceByDefault(basketContext.validatedBasket)}
                                            isConfirmationPage={checkout}
                                            />
                                    )}
                                    { showTipping && checkout && renderGratuity() }
                                    {renderTotal()}
                                </div>
                                {confirmation && total() > 0 ? (
                                    <PaymentCardDetails className={'basket__payment-info'}>
                                        <div className={"basket__payment-info__text"}>
                                            Paid
                                        </div>
                                        <div className={"basket__payment-info__card"}>
                                            <span className={''}>••••{paymentDetails().last4}</span>
                                            <img width={'32'} height={'24'} src={getCardImage(paymentDetails().brand)}/>
                                        </div>
                                    </PaymentCardDetails>

                                ) : null}
                            </>
                        ) : (
                            <p className="text-center p-4 d-flex align-items-center justify-content-center flex-grow-1">{t`Your basket is empty`}</p>
                        )}

                    </OrderSummary>
                </div>
            </BasketCard>
        </BasketContainer>
    );
};

const SpecialInstructions = styled.div`
    display: flex;
    margin-top: 16px;
    padding: 16px;
    border-radius: var(--radius_xl);
    background-color: var(--bg-grey);
    color: var(--text-black);

    .material-symbols-outlined {
        margin-right: 16px;
        font-size: 24px;
        line-height: 24px;
    }

    .instructions {
        font-size: 16px;
        line-height: 24px;
    }
`;

const PaymentCardDetails = styled.div`
    &.basket__payment-info {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        font-size: 16px;
        font-weight: 500;

        @media screen and (min-width: 993px) {
            border-top: 1px solid var(--layouts_container_border);
            padding-bottom: 0;
        }
    }

    .basket__payment-info {
        &__text {
            flex-grow: 1;
        }

        &__card {
            display: flex;
            align-items: center;
            gap: 16px;
        }
    }
`

const OfferHint = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 20px;
    color: var(--text-black);

    .material-symbols-outlined {
        font-size: 20px;
        flex-shrink: 0;
    }
`;

const PromoDetails = styled.div`
    span {
        font-size: 14px;
        font-weight: 500;
    }

    img {
        margin-right: 8px;
        cursor: pointer;
    }
`;

const BasketItems = styled.div`
    font-size: 16px;
`;

const OrderInfo = styled.div<{ checkout?: boolean }>`
    .basket__order-info__title {
        font-size: 20px;
        line-height; 24px;
        font-family: var(--text_body_font);
        letter-spacing: var(--text_body_font_spacing);
        text-transform: var(--text_body_font_case);
    }

    .info-card  {
        display: flex;
        flex-direction: column;
        gap: 8px;
        color: var(--text-black);

        &__channel, &__site_address, &__timeslot {
            font-size: 16px;
            line-height: 24px;
        }

        &__site_address .material-symbols-outlined {
            padding-left: 4px;
            font-size: 24px;
            line-height: 24px;
            vertical-align: bottom;
        }
    }

    img {
        margin-right: 9px;
    }
`;

const OrderSummary = styled.div`
    .basketContainer {
        padding: 16px;
        border-bottom: 1px solid var(--border-grey);
        color: var(--text-black);
    }

    .promoSubmit {
        height: 48px;
        margin: 0;
        padding: 0 23px;
        margin-left: 16px;

        @media screen and (min-width: 768px) {
            padding: 0 40px;
            min-width: 152px;
            margin-left: 24px;
        }
    }

    .subtotalContainer {
        padding: 16px;
    }
`;

const Subtotal = styled.div<{ confirmation: boolean }>`
    font-size: var(--core_text_id_md);
    padding: 0;

    .basket__subtotal {
        display: flex;
        justify-content: space-between;
        width: 100%;
        margin-bottom: 16px;

        span {
            font-weight: 400;
            font-size: 16px;
            line-height: 24px;
            color: var(--text-black);
        }
    }
`;

const DiscountLine = styled.div`
    .non_wrapped {
        white-space: nowrap;
    }
`;

const Total = styled.div<{ confirmation: boolean }>`
    display: flex;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
    padding: 16px 0 0 0;
    border-top: 1px solid var(--layouts_container_border);

    @media screen and (min-width: 993px) {
        padding-bottom: 0;
    }

    span {
        font-weight: bold;
        color: var(--text-black);

        &:first-child {
            display: block;
            width: 100%;
        }
    }
`;

const BasketContainer = styled.div<{ checkout: boolean, confirmation: boolean }>`
    margin: 0;
    padding: 15px;
    flex-grow: 0;

    ${(props) =>
        props.checkout ? `
            margin-top: 0;
            order: 2;
            padding: 0;
            ${BasketCard}{
                width: 100%;
                box-shadow: none;
            }
        ` : `
            @media screen and (max-width: 767px) {
                display: none;
            }
        `
    }
`;

const BasketCard = styled.div`
    width: 100%;
    z-index: 1;
    width: 350px;
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 993px) {
        border-bottom: 0;
        padding: 0;
    }

    @media screen and (min-width: 1140px) {
        top: 84px; //(64 + 20)
    }

    hr {
        width: 100vw;
        position: relative;
        left: -16px;
        margin: 16px 0;

        @media screen and (min-width: 768px) {
            left: -24px;
            max-width: 520px;
        }
    }

    .basket__order-info {
        padding: 32px 0px;

        @media screen and (min-width: 993px) {
            padding: 0;
        }

        &__card {
            padding: 16px;
            border-radius: var(--radius_xl);
            display: flex;
            gap: 16px;

            span {
                font-size: 16px;
                line-height: 24px;
            }

            @media screen and (min-width: 993px) {
                padding: 0;
                background-color: transparent;
            }
        }

        &__title {
            margin-bottom: 16px;
        }
    }

     .basket__order-title {
        padding: 16px;
        border-bottom: 1px solid var(--border-grey);
    }
`;

const StyledForm = styled(Form)``;

export default Basket;
